import { useEffect, useMemo, useRef, useState } from "react";
import ForceGraph2D from "react-force-graph-2d";
import { Link } from "react-router-dom";
import { X, ArrowUpRight, ArrowRight } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";

const INDUSTRY_COLOR = {
  "Artificial Intelligence": "#00E5FF",
  "Nightlife Technology": "#8B5CF6",
  "Culinary Technology": "#F59E0B",
  "Financial Technology / Market Intelligence": "#10B981",
  "Virtual Worlds / Social Technology": "#EC4899",
  "Virtual Culture / Digital Entertainment": "#14B8A6",
  "Social Technology": "#3B82F6",
  "Robotics / Artificial Intelligence": "#C0C0C0",
  "AI Hardware": "#F9E596",
};

function buildGraph(projects) {
  const nodes = [{ id: "ImJove Tech", type: "root", val: 34, color: "#D4AF37" }];
  const links = [];
  const industries = {};
  projects.forEach((p) => {
    const ind = p.industry || "Technology";
    if (!industries[ind]) {
      industries[ind] = true;
      nodes.push({ id: ind, type: "industry", val: 14, color: INDUSTRY_COLOR[ind] || "#A1A1AA" });
      links.push({ source: "ImJove Tech", target: ind });
    }
    nodes.push({ id: p.name, type: "project", val: 9, color: p.accent || "#fff", project: p });
    links.push({ source: ind, target: p.name });
  });
  return { nodes, links };
}

export default function UniverseGraph({ projects, height = 620 }) {
  const wrapRef = useRef(null);
  const fgRef = useRef(null);
  const [dims, setDims] = useState({ w: 800, h: height });
  const [selected, setSelected] = useState(null);
  const data = useMemo(() => buildGraph(projects), [projects]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setDims({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setDims({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fgRef.current?.zoomToFit(500, 70), 700);
    return () => clearTimeout(t);
  }, [data, dims.w]);

  return (
    <div ref={wrapRef} data-testid="universe-graph" className="relative h-full w-full overflow-hidden rounded-2xl border border-white/10 bg-ink-900" style={{ height }}>
      <ForceGraph2D
        ref={fgRef}
        width={dims.w}
        height={dims.h}
        graphData={data}
        backgroundColor="rgba(0,0,0,0)"
        linkColor={() => "rgba(255,255,255,0.12)"}
        linkDirectionalParticles={2}
        linkDirectionalParticleWidth={1.6}
        linkDirectionalParticleColor={() => "rgba(212,175,55,0.5)"}
        cooldownTicks={120}
        onNodeClick={(n) => n.type === "project" && setSelected(n.project)}
        nodeCanvasObject={(node, ctx, scale) => {
          const r = Math.sqrt(node.val) * 1.6;
          ctx.beginPath();
          ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
          ctx.fillStyle = node.color;
          ctx.shadowColor = node.color;
          ctx.shadowBlur = node.type === "root" ? 24 : 10;
          ctx.fill();
          ctx.shadowBlur = 0;
          if (node.type === "root") {
            ctx.strokeStyle = "rgba(249,229,150,0.7)";
            ctx.lineWidth = 1.2;
            ctx.stroke();
          }
          const label = node.id;
          const fs = (node.type === "root" ? 6 : node.type === "industry" ? 4.2 : 3.6);
          ctx.font = `${node.type === "root" ? 700 : 500} ${fs}px Outfit, sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          ctx.fillStyle = node.type === "project" ? "rgba(255,255,255,0.7)" : "#fff";
          ctx.fillText(label, node.x, node.y + r + 2);
        }}
        nodePointerAreaPaint={(node, color, ctx) => {
          const r = Math.sqrt(node.val) * 1.6 + 3;
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
          ctx.fill();
        }}
      />

      <div className="pointer-events-none absolute bottom-3 left-4 font-mono2 text-[10px] uppercase tracking-widest text-white/30">
        Drag · Zoom · Click a product
      </div>

      {selected && (
        <div
          data-testid="universe-panel"
          className="glass absolute right-3 top-3 z-20 w-[300px] max-w-[85%] rounded-xl p-5"
          style={{ borderColor: `${selected.accent}55` }}
        >
          <button onClick={() => setSelected(null)} className="absolute right-3 top-3 text-white/50 hover:text-white" data-testid="universe-panel-close">
            <X size={16} />
          </button>
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: selected.accent }} />
            <h4 className="font-display text-lg font-semibold text-white">{selected.name}</h4>
          </div>
          <p className="mt-1 font-mono2 text-[10px] uppercase tracking-widest text-white/40">{selected.industry}</p>
          <div className="mt-3"><StatusBadge status={selected.status} /></div>
          <p className="mt-3 font-body text-sm leading-relaxed text-white/60">{selected.description}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to={`/technology/${selected.slug}`} className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 font-mono2 text-[10px] uppercase tracking-widest">
              Learn More <ArrowRight size={12} />
            </Link>
            {selected.website_url && (
              <a href={selected.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 font-mono2 text-[10px] uppercase tracking-widest text-black" style={{ background: selected.accent }}>
                Visit <ArrowUpRight size={12} />
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
